import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard, Building2, FileText, Bell,
  MoreHorizontal, Store, Crown, Wallet, History,
  MessageSquareMore, BookOpen, Receipt, AlertCircle,
  AlertTriangle, Banknote, ShieldMinus, ShieldPlus,
  Calendar, CalendarClock, Calculator, Settings, X,
  Mail, CircleDollarSign, Users, ServerCog,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store/app.store';

// ── Fixed bottom tabs ─────────────────────────────────────────────────────────

const MAIN_TABS = [
  {
    path:  '/dashboard',
    label: 'Inicio',
    icon:  LayoutDashboard,
    match: ['/dashboard'],
  },
  {
    path:  '/empresas',
    label: 'Empresas',
    icon:  Building2,
    match: ['/empresas'],
  },
  {
    path:  '/declaraciones',
    label: 'Declaraciones',
    icon:  FileText,
    match: ['/declaraciones'],
  },
  {
    path:  '/buzon',
    label: 'Alertas',
    icon:  Bell,
    match: ['/buzon', '/deudas', '/multas'],
  },
] as const;

// ── "Más" sheet — espejo completo del sidebar ─────────────────────────────────

type TenantRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';

interface MoreItem {
  path:         string;
  label:        string;
  icon:         React.ComponentType<{ className?: string }>;
  masterOnly?:  boolean;
  tenantRoles?: TenantRole[];
}

interface MoreSection {
  label: string;
  items: MoreItem[];
}

const MORE_SECTIONS: MoreSection[] = [
  {
    label: 'Principal',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/empresas',  label: 'Empresas',  icon: Building2 },
    ],
  },
  {
    label: 'Estudio Contable',
    items: [
      { path: '/estudio',            label: 'Mi Estudio',     icon: Store },
      { path: '/paquetes',           label: 'Suscripción',    icon: Crown,             tenantRoles: ['OWNER', 'ADMIN'] },
      { path: '/cobranzas',          label: 'Cobranzas',      icon: Wallet,            tenantRoles: ['OWNER', 'ADMIN'] },
      { path: '/comunicaciones',     label: 'Comunicaciones', icon: History,           tenantRoles: ['OWNER', 'ADMIN'] },
      { path: '/whatsapp-templates', label: 'Plantillas WA',  icon: MessageSquareMore },
    ],
  },
  {
    label: 'Cumplimiento',
    items: [
      { path: '/sire',          label: 'SIRE',            icon: BookOpen },
      { path: '/declaraciones', label: 'Declaraciones',   icon: FileText },
      { path: '/liquidacion',   label: 'Liquidación',     icon: Receipt },
    ],
  },
  {
    label: 'Recaudación',
    items: [
      { path: '/detracciones', label: 'Detracciones', icon: Banknote },
      { path: '/retenciones',  label: 'Retenciones',  icon: ShieldMinus },
      { path: '/percepciones', label: 'Percepciones', icon: ShieldPlus },
    ],
  },
  {
    label: 'Alertas',
    items: [
      { path: '/buzon',  label: 'Buzón SUNAT', icon: Mail },
      { path: '/deudas', label: 'Deudas',      icon: AlertCircle },
      { path: '/multas', label: 'Multas',      icon: AlertTriangle },
    ],
  },
  {
    label: 'Herramientas',
    items: [
      { path: '/calendario',       label: 'Calendario',  icon: Calendar },
      { path: '/cronograma-sunat', label: 'Cronograma',  icon: CalendarClock },
      { path: '/calculadora',      label: 'Calculadora', icon: Calculator },
      { path: '/uit',              label: 'Tabla UIT',   icon: CircleDollarSign },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { path: '/configuracion',  label: 'Configuración', icon: Settings },
      { path: '/usuarios',       label: 'Usuarios',      icon: Users, masterOnly: true },
      { path: '/sistema/worker', label: 'Colas y Cron',  icon: ServerCog, masterOnly: true },
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function MobileBottomNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { user, tenants, activeTenantId } = useAppStore();
  const isMaster = user?.systemRole === 'MASTER';
  const tenantRole = (tenants.find(t => t.id === activeTenantId)?.role ?? 'VIEWER') as TenantRole;
  const canManageCobranzas = tenants.find(t => t.id === activeTenantId)?.canManageCobranzas ?? false;

  const isMoreActive = !MAIN_TABS.some(t => t.match.some(m => currentPath.startsWith(m)));

  return (
    <>
      {/* ── Bottom tab bar ────────────────────────────────────────────────── */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background border-t border-border flex items-stretch"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {MAIN_TABS.map(tab => {
          const active = tab.match.some(m => currentPath.startsWith(m));
          const Icon   = tab.icon;
          return (
            <Link
              key={tab.path}
              to={tab.path}
              className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
              onClick={() => { setMoreOpen(false); }}
            >
              {active && (
                <span className="absolute top-0 inset-x-3 h-0.5 rounded-full bg-[#1f5d86]" />
              )}
              <Icon className={cn(
                'h-5 w-5 transition-colors',
                active ? 'text-[#1f5d86]' : 'text-muted-foreground',
              )} />
              <span className={cn(
                'text-[10px] font-medium leading-none',
                active ? 'text-[#1f5d86]' : 'text-muted-foreground',
              )}>
                {tab.label}
              </span>
            </Link>
          );
        })}

        {/* Más */}
        <button
          onClick={() => { setMoreOpen(v => !v); }}
          className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
        >
          {(moreOpen || isMoreActive) && (
            <span className="absolute top-0 inset-x-3 h-0.5 rounded-full bg-[#1f5d86]" />
          )}
          <MoreHorizontal className={cn(
            'h-5 w-5 transition-colors',
            (moreOpen || isMoreActive) ? 'text-[#1f5d86]' : 'text-muted-foreground',
          )} />
          <span className={cn(
            'text-[10px] font-medium leading-none',
            (moreOpen || isMoreActive) ? 'text-[#1f5d86]' : 'text-muted-foreground',
          )}>
            Más
          </span>
        </button>
      </nav>

      {/* ── "Más" bottom sheet ────────────────────────────────────────────── */}
      {moreOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-xs"
            onClick={() => { setMoreOpen(false); }}
          />

          {/* Panel */}
          <div
            className="lg:hidden fixed inset-x-0 z-50 bg-background rounded-t-2xl shadow-2xl border-t border-border flex flex-col"
            style={{
              bottom: 'calc(56px + env(safe-area-inset-bottom))',
              maxHeight: '72dvh',
            }}
          >
            {/* Handle + header */}
            <div className="relative flex items-center justify-between px-4 pt-4 pb-3 border-b shrink-0">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-muted-foreground/20" />
              <p className="text-sm font-semibold">Menú</p>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => { setMoreOpen(false); }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Scrollable content — todas las secciones del sidebar */}
            <div className="overflow-y-auto flex-1 py-2">
              {MORE_SECTIONS.map(section => {
                const visibleItems = section.items.filter(item => {
                  if (item.masterOnly && !isMaster) return false;
                  if (item.tenantRoles && !isMaster) {
                    // Cobranzas: un MEMBER ("Asistente") puntual puede tener acceso vía el
                    // override canManageCobranzas de su ficha, sin necesitar el rol ADMIN.
                    const cobranzaOverride = item.path === '/cobranzas' && tenantRole === 'MEMBER' && canManageCobranzas;
                    if (!item.tenantRoles.includes(tenantRole) && !cobranzaOverride) return false;
                  }
                  return true;
                });
                if (visibleItems.length === 0) return null;

                return (
                  <div key={section.label} className="px-3 pb-3">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground/50 px-1 mb-2 mt-1">
                      {section.label}
                    </p>
                    <div className="grid grid-cols-4 gap-1">
                      {visibleItems.map(item => {
                        const active = currentPath.startsWith(item.path);
                        const Icon   = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => { setMoreOpen(false); }}
                            className={cn(
                              'flex flex-col items-center gap-1 rounded-xl py-2.5 px-1 transition-colors text-center',
                              active
                                ? 'bg-[#eaf2fa] dark:bg-[#1f5d86]/20'
                                : 'hover:bg-muted',
                            )}
                          >
                            <Icon className={cn(
                              'h-[22px] w-[22px] shrink-0',
                              active ? 'text-[#1f5d86]' : 'text-muted-foreground',
                            )} />
                            <span className={cn(
                              'text-[10px] font-medium leading-tight line-clamp-2 w-full',
                              active ? 'text-[#1f5d86]' : 'text-foreground/70',
                            )}>
                              {item.label}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
